// Native Run A, Part 3 (docs/NATIVE_RUN_A_SPEC.md) — where this build talks to, where its
// links point, and how it loads photos.
//
// The web app and the API share one origin, so every call in src/ used to be a server-relative
// path. The Capacitor app is a different origin (capacitor://localhost, https://localhost) and
// its calls have to say which server they mean. Two esbuild defines decide that at build time;
// lib/buildFlags.js reads them (dependency-free, so content.js and speech.js can import it)
// and this module re-exports them beside the photo grant, which needs api.js:
//
//   apiUrl(path)        a server-relative path as a URL this build can fetch (identity on the web)
//   publicOrigin()      the web origin for links people share, scan or open
//   publicPageHref(p)   an href for /terms, /privacy, /help (relative on the web)
//   imageSrc(url)       a stored photo URL as this build can render it (identity on the web)
//   IS_NATIVE           for the few places that must branch
//
// Under the web defines every one of these is identity, which is what keeps the web app
// unchanged by anything native (the first rule of that spec; scripts/check-native.mjs §7).
// Nothing in src/ imports a Capacitor module: the native-only pieces here are an origin, a
// grant and a boolean; the native bootstrap (native/entry.jsx) does the plugin work.
import { householdFetch } from "./api.js";
import { API_ORIGIN, IS_NATIVE, apiUrl, publicOrigin, publicPageHref } from "./buildFlags.js";

export { IS_NATIVE, apiUrl, publicOrigin, publicPageHref };
const NATIVE = IS_NATIVE;

/* ------------------------------ photos in a native build ------------------------------
 * The PWA loads /images/ with the lb_img cookie. A native WebView has no such cookie, so
 * every photo URL is rendered with a short-lived grant (worker/lib/imageGrant.js):
 *
 *   /images/ABC123/x.jpg  →  https://<api>/images/ABC123/x.jpg?t=…&exp=…
 *
 * One grant per household, cached here; asked for the first time a photo of that household
 * is rendered and refreshed before it expires. While none is held imageSrc returns a
 * transparent pixel and the tree re-renders when the grant arrives (useImageGrantRefresh,
 * mounted once in Root). On the web none of this runs: imageSrc is identity.
 * ---------------------------------------------------------------------------------- */
const PLACEHOLDER = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
const REFRESH_AHEAD_MS = 5 * 60 * 1000;
const GRANT_EVENT = "lb-image-grant";
const grants = new Map();   // code → { t, exp }
const inFlight = new Map(); // code → Promise
const timers = new Map();   // code → refresh timeout

function grantFor(code) {
  const g = grants.get(code);
  return g && g.exp * 1000 - Date.now() > 30 * 1000 ? g : null;
}

// LB-418 / SR2-25 (security fix run 2, Part 7): the ONLY shape a stored photo has. The upload
// route answers `/images/{code}/{uuid}.{jpg|png|webp}` (worker/handlers/images.js) and nothing
// else ever writes one, so a value in any other form was put there by a client straight into
// the saved state. Rendering such a value as stored let one household member point every
// family device at a third-party address (a tracking pixel, with nothing a parent could see).
// The worker now nulls such fields on save; this is the client's half, so a value already in
// a cached or stale copy renders as the transparent pixel rather than fetched.
export const STORED_IMAGE_RE = /^\/images\/[A-Z0-9]{6}\/[0-9a-f-]+\.(jpg|jpeg|png|webp)$/;

export function imageSrc(url) {
  // Nothing to render stays nothing: every render site guards with `image ? <img …> : …`, and
  // an empty value reaching an <img> is the same no-request whether it is null or a pixel.
  if (typeof url !== "string" || url === "") return url;
  if (!STORED_IMAGE_RE.test(url)) return PLACEHOLDER;
  if (!NATIVE) return url;
  const code = url.slice("/images/".length).split("/")[0];
  const g = grantFor(code);
  if (g) return `${API_ORIGIN}${url}?t=${encodeURIComponent(g.t)}&exp=${g.exp}`;
  requestImageGrant(code);
  return PLACEHOLDER;
}

// Asks the worker for a grant, once per household at a time. Non-fatal: a refusal or a
// network blip leaves the placeholder up and the next render asks again.
export function requestImageGrant(code) {
  if (!NATIVE || !code) return Promise.resolve(null);
  if (inFlight.has(code)) return inFlight.get(code);
  const p = (async () => {
    try {
      const res = await householdFetch(code, "/image-grant", { method: "POST" });
      if (!res.ok) return null;
      const data = await res.json().catch(() => null);
      if (!data || typeof data.t !== "string" || !Number.isFinite(data.exp)) return null;
      grants.set(code, { t: data.t, exp: data.exp });
      scheduleRefresh(code, data.exp);
      try { window.dispatchEvent(new CustomEvent(GRANT_EVENT, { detail: { code } })); } catch { /* no window */ }
      return grants.get(code);
    } catch {
      return null;
    } finally {
      inFlight.delete(code);
    }
  })();
  inFlight.set(code, p);
  return p;
}

function scheduleRefresh(code, exp) {
  clearTimeout(timers.get(code));
  const delay = Math.max(1000, exp * 1000 - Date.now() - REFRESH_AHEAD_MS);
  timers.set(code, setTimeout(() => requestImageGrant(code), delay));
}

// Mounted once, in Root, with the household code. Native only: prefetches the grant as soon
// as the household is known (so photos rarely show the placeholder at all), re-renders the
// tree when one arrives, and re-checks on every foreground in case the app slept past the
// refresh timer. A no-op on the web beyond the hooks themselves.
export function useImageGrantRefresh(code) {
  const [, bump] = React.useState(0);
  React.useEffect(() => {
    if (!NATIVE || !code) return undefined;
    requestImageGrant(code);
    const onGrant = () => bump((n) => n + 1);
    const onVisible = () => { if (document.visibilityState === "visible" && !grantFor(code)) requestImageGrant(code); };
    window.addEventListener(GRANT_EVENT, onGrant);
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.removeEventListener(GRANT_EVENT, onGrant); document.removeEventListener("visibilitychange", onVisible); };
  }, [code]);
}

// For the check only.
export function _grantState() { return { native: NATIVE, apiOrigin: API_ORIGIN, grants: new Map(grants) }; }
